import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
} from '@mui/material';
import {
  LooksOne,
  LooksTwo,
  Looks3,
  Looks4,
  Looks5,
  Looks6,
  TipsAndUpdates,
  Warning,
} from '@mui/icons-material';

// Step number icons
const stepIcons = [LooksOne, LooksTwo, Looks3, Looks4, Looks5, Looks6];

/**
 * Renders a documentation section with heading, text, steps, images, and tips.
 */
const DocsSection = ({ section, sectionKey }) => {
  if (!section || !section.content) {
    return null;
  }

  return (
    <Box sx={{ mb: 6 }}>
      <Typography
        variant="h4"
        component="h2"
        id={sectionKey}
        sx={{
          mb: 3,
          pb: 1,
          borderBottom: '2px solid',
          borderColor: 'primary.main',
          fontWeight: 600,
        }}
      >
        {section.title}
      </Typography>

      {section.content.map((item, index) => (
        <Box key={index} sx={{ mb: 4 }}>
          {/* Heading */}
          {item.heading && (
            <Typography
              variant="h5"
              component="h3"
              sx={{ mb: 2, fontWeight: 500, color: 'primary.dark' }}
            >
              {item.heading}
            </Typography>
          )}

          {/* Main text */}
          {item.text && (
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                lineHeight: 1.8,
                whiteSpace: 'pre-line', // Preserve newlines
              }}
            >
              {item.text}
            </Typography>
          )}

          {/* Numbered steps */}
          {item.steps && item.steps.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2, p: 2, bgcolor: 'grey.50' }}>
              <List dense>
                {item.steps.map((step, stepIndex) => {
                  const IconComponent = stepIcons[stepIndex] || LooksOne;
                  return (
                    <ListItem key={stepIndex} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <IconComponent color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={step} />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          )}

          {/* Screenshot image */}
          {item.image && (
            <Box sx={{ mb: 2 }}>
              <Paper
                elevation={2}
                sx={{
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  maxWidth: '100%',
                }}
              >
                <Box
                  component="img"
                  src={item.image}
                  alt={item.imageAlt || item.heading || 'Screenshot'}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                  onError={(e) => {
                    // Hide broken images gracefully
                    e.target.style.display = 'none';
                  }}
                />
              </Paper>
              {item.imageAlt && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
                >
                  {item.imageAlt}
                </Typography>
              )}
            </Box>
          )}

          {/* Tip box */}
          {item.tip && (
            <Alert
              severity="info"
              icon={<TipsAndUpdates />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">{item.tip}</Typography>
            </Alert>
          )}

          {/* Warning box */}
          {item.warning && (
            <Alert
              severity="warning"
              icon={<Warning />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">{item.warning}</Typography>
            </Alert>
          )}

          {/* Divider between items (except last) */}
          {index < section.content.length - 1 && (
            <Divider sx={{ my: 3 }} />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default DocsSection;
